import React, { useState, useEffect, useCallback } from "react";
import { getTeam, createTeamUser, testTeamKey, deleteTeamUser, syncTeam } from "../../api";
import { useToast } from "../../hooks/use-toast";
import { Plus, Trash2, Users, KeyRound, CheckCircle2, XCircle, Loader2, RefreshCw, ShieldCheck, Shield, HardHat, Link2 } from "lucide-react";
import ProfileEditor from "./team/ProfileEditor";
import ServiceBadge from "./ServiceBadge";
import { memberServices } from "../../lib/services";
import { log } from "../../lib/log";

const empty = { nom: "", login: "", api_key: "", email: "" };
const inputCls = "w-full border border-neutral-300 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-brand";

const LEVELS = {
  superadmin: { label: "Super-admin", cls: "bg-neutral-900 text-white", icon: ShieldCheck, desc: "CRM complet + paramètres" },
  major: { label: "Major", cls: "bg-brand text-white", icon: Shield, desc: "CRM fonctionnel, RDV, agenda" },
  easer: { label: "Easer (intervenant)", cls: "bg-emerald-100 text-emerald-700", icon: HardHat, desc: "App terrain uniquement" },
};

const LevelBadge = ({ level }) => {
  const m = LEVELS[level] || { label: "Non synchronisé", cls: "bg-neutral-100 text-neutral-500", icon: Shield };
  const Icon = m.icon;
  return <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${m.cls}`} data-testid={`team-level-${level || "none"}`}><Icon size={11} /> {m.label}</span>;
};

const UserRow = ({ u, testing, onTest, onRemove, onSaved }) => (
  <div className={`bg-white border border-neutral-200 rounded-xl p-4 flex flex-wrap items-center gap-3 ${u.active === false ? "opacity-50" : ""}`} data-testid={`team-user-${u.id}`}>
    <div className="bg-brand/10 text-brand rounded-full w-10 h-10 flex items-center justify-center font-heading font-bold shrink-0">
      {(u.nom || "?").charAt(0).toUpperCase()}
    </div>
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-2 flex-wrap">
        <p className="font-semibold text-neutral-900">{u.nom}</p>
        <LevelBadge level={u.level} />
        {memberServices(u).map((s) => <ServiceBadge key={s} service={s} testid={`team-service-${u.id}-${s}`} />)}
        {u.services_source === "manual" && <span className="text-[10px] text-neutral-400">(service défini manuellement)</span>}
        {u.services_source === "default" && u.level !== "superadmin" && <span className="text-[10px] text-amber-600" title="Aucun tag « easer clean » / « easer green » dans Dolibarr">(par défaut, sans tag Dolibarr)</span>}
        {u.active === false && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700">Désactivé dans Dolibarr</span>}
      </div>
      <p className="text-sm text-neutral-500 truncate">
        {u.login}{u.email ? ` · ${u.email}` : " · pas d'email (rappels impossibles)"}
        {u.groups?.length > 0 && ` · groupes : ${u.groups.join(", ")}`}
        {u.tags?.length > 0 && ` · tags : ${u.tags.join(", ")}`}
      </p>
      <ProfileEditor u={u} onSaved={onSaved} />
    </div>
    <div className="flex items-center gap-3 shrink-0">
      {u.has_key ? (
        <span className={`flex items-center gap-1 text-xs font-semibold ${u.key_valid ? "text-emerald-600" : "text-amber-600"}`}>
          {u.key_valid ? <CheckCircle2 size={14} /> : <XCircle size={14} />} clé {u.api_key_masked}
        </span>
      ) : (
        <span className="flex items-center gap-1 text-xs text-neutral-400" title="La clé est liée automatiquement à sa première connexion"><Link2 size={13} /> clé liée à la 1re connexion</span>
      )}
      {u.has_key && (
        <button onClick={() => onTest(u.id)} disabled={testing === u.id} className="text-sm text-brand hover:underline flex items-center gap-1">
          {testing === u.id ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />} Tester
        </button>
      )}
      <button onClick={() => onRemove(u.id)} className="text-neutral-400 hover:text-red-500 p-1" data-testid={`team-delete-${u.id}`}><Trash2 size={16} /></button>
    </div>
  </div>
);

const ManualForm = ({ onSubmit, loading }) => {
  const [form, setForm] = useState(empty);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => { e.preventDefault(); if (await onSubmit(form)) setForm(empty); };
  return (
    <form onSubmit={submit} className="bg-white border border-neutral-200 rounded-xl p-5 mb-5 grid sm:grid-cols-3 gap-3 items-end" data-testid="team-manual-form">
      <div><label className="block text-xs font-semibold text-neutral-600 mb-1">Nom</label><input required className={inputCls} value={form.nom} onChange={set("nom")} /></div>
      <div><label className="block text-xs font-semibold text-neutral-600 mb-1">Identifiant Dolibarr</label><input required className={inputCls} placeholder="ex: erp.test" value={form.login} onChange={set("login")} /></div>
      <div><label className="block text-xs font-semibold text-neutral-600 mb-1">Clé API Dolibarr</label><input required className={inputCls} value={form.api_key} onChange={set("api_key")} /></div>
      <div className="sm:col-span-3"><label className="block text-xs font-semibold text-neutral-600 mb-1">Email (rappels)</label><input type="email" className={inputCls} value={form.email} onChange={set("email")} data-testid="team-email-input" /></div>
      <button type="submit" disabled={loading} className="sm:col-span-3 bg-brand hover:bg-brand-dark text-white font-semibold py-2.5 rounded-md disabled:opacity-60">{loading ? "Vérification de la clé..." : "Enregistrer"}</button>
    </form>
  );
};

const TeamPanel = () => {
  const [users, setUsers] = useState([]);
  const [sync, setSync] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [testing, setTesting] = useState(null);
  const { toast } = useToast();

  const load = useCallback(async () => {
    try {
      const res = await getTeam();
      setUsers(res.data.users || []);
      setSync(res.data.sync || {});
    } catch (err) {
      log.error("Chargement des utilisateurs :", err);
      toast({ title: "Utilisateurs indisponibles", variant: "destructive" });
    }
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  const doSync = async () => {
    setSyncing(true);
    try {
      const r = await syncTeam();
      toast({ title: "Synchronisation Dolibarr ✓", description: `${r.data.count} utilisateur(s) importé(s) avec leurs droits.` });
      load();
    } catch (err) {
      toast({ title: "Synchronisation impossible", description: err?.response?.data?.detail || "Dolibarr injoignable", variant: "destructive" });
    } finally { setSyncing(false); }
  };

  const add = async (form) => {
    setLoading(true);
    try {
      const res = await createTeamUser(form);
      setShowForm(false);
      load();
      toast({ title: "Utilisateur ajouté ✓", description: res.data.key_valid ? "Clé API valide." : "⚠️ Clé API non valide.", variant: res.data.key_valid ? undefined : "destructive" });
      return true;
    } catch (err) {
      toast({ title: "Erreur", description: err?.response?.data?.detail || "Ajout impossible", variant: "destructive" });
      return false;
    } finally { setLoading(false); }
  };

  const test = async (id) => {
    setTesting(id);
    try {
      const res = await testTeamKey(id);
      setUsers((list) => list.map((u) => (u.id === id ? { ...u, key_valid: res.data.valid } : u)));
      toast({ title: res.data.valid ? "Clé valide ✓" : "Clé invalide", variant: res.data.valid ? undefined : "destructive" });
    } finally { setTesting(null); }
  };

  const remove = async (id) => { await deleteTeamUser(id); load(); };
  const counts = Object.keys(LEVELS).map((k) => [k, users.filter((u) => u.level === k && u.active !== false).length]);

  return (
    <div data-testid="team-panel">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Users size={20} className="text-brand" />
          <h2 className="font-heading font-bold text-xl text-neutral-900">Utilisateurs (Dolibarr)</h2>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => setShowForm(!showForm)} className="text-sm text-neutral-600 hover:text-brand flex items-center gap-1.5" data-testid="team-manual-toggle"><Plus size={15} /> Ajout manuel</button>
          <button onClick={doSync} disabled={syncing} data-testid="team-sync-btn" className="flex items-center gap-2 bg-brand hover:bg-brand-dark text-white text-sm font-semibold px-4 py-2 rounded-md disabled:opacity-60">
            {syncing ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />} Synchroniser depuis Dolibarr
          </button>
        </div>
      </div>
      <p className="text-sm text-neutral-500 mb-1">
        Les utilisateurs et leurs droits sont repris de Dolibarr (synchro automatique toutes les 6 h) : administrateur Dolibarr → Super-admin,
        groupe « major » → Major, autres → Easer. Le <b>service</b> de chaque easer vient de ses tags Dolibarr : « easer clean » → Nettoyage,
        « easer green » → Espaces verts (les deux tags = polyvalent ; sans tag = Nettoyage). Chacun se connecte avec sa clé API Dolibarr personnelle, liée automatiquement à sa première connexion.
        L'adresse de résidence et les heures hebdo contractuelles sont reprises de Dolibarr ; le rayon d'intervention (défaut 40 km) sert au remplacement automatique.
      </p>
      <p className="text-xs text-neutral-400 mb-4" data-testid="team-sync-state">
        {sync.last_sync ? `Dernière synchro : ${new Date(sync.last_sync).toLocaleString("fr-FR")} · ${sync.count} utilisateur(s)` : "Jamais synchronisé"}
        {sync.error && <span className="text-red-600"> · erreur : {sync.error}</span>}
      </p>

      <div className="grid sm:grid-cols-3 gap-3 mb-5">
        {counts.map(([k, n]) => (
          <div key={k} className="bg-white border border-neutral-200 rounded-xl p-3 flex items-center justify-between" data-testid={`team-count-${k}`}>
            <div><LevelBadge level={k} /><p className="text-xs text-neutral-500 mt-1">{LEVELS[k].desc}</p></div>
            <span className="font-heading font-extrabold text-2xl text-neutral-900">{n}</span>
          </div>
        ))}
      </div>

      {showForm && <ManualForm onSubmit={add} loading={loading} />}

      {users.length === 0 ? (
        <div className="text-center text-neutral-400 py-10">
          <KeyRound size={40} className="mx-auto mb-3 opacity-40" />
          <p className="text-sm">Aucun utilisateur. Cliquez sur « Synchroniser depuis Dolibarr ».</p>
        </div>
      ) : (
        <div className="space-y-3">{users.map((u) => <UserRow key={u.id} u={u} testing={testing} onTest={test} onRemove={remove} onSaved={(d) => setUsers((l) => l.map((x) => (x.id === d.id ? { ...x, ...d } : x)))} />)}</div>
      )}
    </div>
  );
};

export default TeamPanel;
